import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { CopyableCode } from '@/components/CopyableCode'
import { FieldLabel } from '@/components/FieldLabel'
import { useNetwork } from '@/contexts/NetworkContext'
import { conversionPixel, conversionScript } from '@/services/creatives/conversionTags'
import type { RetailProduct } from '@/services/productFeed/productFeedApi'

type ConversionTarget = 'ITEM_ID' | 'BRAND'

interface RetailConversionTagsDialogProps {
  /** The product to show the code for; the dialog is open while one is given. */
  product: RetailProduct | null
  onClose: () => void
}

/**
 * The code a merchant puts on its confirmation page, so a sale is credited to
 * the retail ads for the product — or for any of the brand's products.
 */
export function RetailConversionTagsDialog({ product, onClose }: RetailConversionTagsDialogProps) {
  const { t } = useTranslation()
  const { currentNetwork } = useNetwork()
  const [picked, setPicked] = useState<ConversionTarget>('ITEM_ID')

  // Fall back to the brand when the product has no id, and back again.
  const target: ConversionTarget =
    picked === 'ITEM_ID' && !product?.itemId && product?.brand
      ? 'BRAND'
      : picked === 'BRAND' && !product?.brand
        ? 'ITEM_ID'
        : picked
  const adSource = (target === 'BRAND' ? product?.brand : product?.itemId) ?? ''
  const options = { networkTagId: currentNetwork?.tagId ?? '', adSource }

  const targets: { id: ConversionTarget; label: string; value?: string }[] = [
    { id: 'ITEM_ID', label: t('retailProducts.conversionByProduct'), value: product?.itemId },
    { id: 'BRAND', label: t('retailProducts.conversionByBrand'), value: product?.brand },
  ]

  return (
    <Dialog open={!!product} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-card sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('retailProducts.conversionTags')}</DialogTitle>
          <DialogDescription>
            {t('retailProducts.conversionTagsDescription', { name: product?.title ?? '' })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <FieldLabel tooltip={t('retailProducts.conversionTargetHelp')}>
              {t('retailProducts.conversionTarget')}
            </FieldLabel>
            <RadioGroup
              value={target}
              onValueChange={(v) => setPicked(v as ConversionTarget)}
              className="gap-2"
            >
              {targets.map((option) => (
                <div key={option.id} className="flex items-start gap-2">
                  <RadioGroupItem
                    value={option.id}
                    id={`retail-conversion-${option.id}`}
                    disabled={!option.value}
                    className="mt-0.5"
                  />
                  <Label
                    htmlFor={`retail-conversion-${option.id}`}
                    className="flex-col items-start gap-0.5"
                  >
                    <span className="text-sm font-medium">{option.label}</span>
                    <span className="font-mono text-xs font-normal text-muted-foreground">
                      {option.value || t('retailProducts.conversionNoValue')}
                    </span>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <Tabs defaultValue="script">
            <TabsList>
              <TabsTrigger value="script">{t('retailProducts.conversionScript')}</TabsTrigger>
              <TabsTrigger value="pixel">{t('retailProducts.conversionPixel')}</TabsTrigger>
            </TabsList>
            <TabsContent value="script" className="pt-3">
              <CopyableCode code={conversionScript(options)} />
            </TabsContent>
            <TabsContent value="pixel" className="pt-3">
              <CopyableCode code={conversionPixel(options)} />
            </TabsContent>
          </Tabs>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
